import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_ENDPOINTS, apiClient } from '../config/api';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const csrfTokenRef = React.useRef(null);
  const csrfTokenPromiseRef = React.useRef(null);

  const ensureCsrfToken = async () => {
    if (csrfTokenRef.current) return csrfTokenRef.current;
    if (csrfTokenPromiseRef.current) return csrfTokenPromiseRef.current;

    csrfTokenPromiseRef.current = apiClient
      .get(API_ENDPOINTS.CSRF_TOKEN)
      .then((res) => {
        const csrfToken = res?.data?.csrf_token;
        if (csrfToken) {
          csrfTokenRef.current = csrfToken;
          apiClient.defaults.headers.common['X-CSRF-Token'] = csrfToken;
        }
        return csrfToken;
      })
      .catch(() => null)
      .finally(() => {
        csrfTokenPromiseRef.current = null;
      });

    return csrfTokenPromiseRef.current;
  };

  /**
   * AUTH-CRITICAL: Logout user and clear token
   * Called when token is invalid or expired
   */
  const logout = () => {
    logger.info('Logging out user - clearing token');
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  useEffect(() => {
    // Prime CSRF token early so login/register don't fail.
    ensureCsrfToken();

    if (token) {
      // Verify token and get user info
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          logger.warn('Failed to parse stored user', e);
          logout();
        }
      }
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password, retryCount = 0) => {
    try {
      // PRODUCTION FIX: Ensure CSRF token before login
      await ensureCsrfToken();
      
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true };
      
    } catch (error) {
      // PRODUCTION FIX: Retry once if CSRF token issue (401/403)
      const status = error.response?.status;
      if ((status === 401 || status === 403) && retryCount === 0) {
        // Clear CSRF token and retry
        csrfTokenRef.current = null;
        delete apiClient.defaults.headers.common['X-CSRF-Token'];
        await ensureCsrfToken();
        return login(email, password, 1);
      }
      
      // Return user-friendly error
      let errorMsg = 'Login failed';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Cannot connect to server. Please check your connection.';
      } else if (status === 401) {
        errorMsg = 'Invalid email or password';
      }
      
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, password, name, phone, retryCount = 0) => {
    try {
      // PRODUCTION FIX: Ensure CSRF token before register
      await ensureCsrfToken();
      
      const response = await apiClient.post(API_ENDPOINTS.REGISTER, {
        email,
        password,
        name,
        phone,
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true };
      
    } catch (error) {
      // PRODUCTION FIX: Retry once if CSRF token issue
      const status = error.response?.status;
      if ((status === 401 || status === 403) && retryCount === 0) {
        csrfTokenRef.current = null;
        delete apiClient.defaults.headers.common['X-CSRF-Token'];
        await ensureCsrfToken();
        return register(email, password, name, phone, 1);
      }
      
      let errorMsg = 'Registration failed';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Cannot connect to server. Please check your connection.';
      }
      
      return { success: false, error: errorMsg };
    }
  };

  const googleLogin = async (googleToken, retryCount = 0) => {
    try {
      await ensureCsrfToken();
      
      const response = await apiClient.post(API_ENDPOINTS.GOOGLE_LOGIN, {
        token: googleToken,
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true };
      
    } catch (error) {
      const status = error.response?.status;
      if ((status === 401 || status === 403) && retryCount === 0) {
        csrfTokenRef.current = null;
        delete apiClient.defaults.headers.common['X-CSRF-Token'];
        await ensureCsrfToken();
        return googleLogin(googleToken, 1);
      }
      
      let errorMsg = 'Google authentication failed';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Cannot connect to server. Please check your connection.';
      }
      
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    googleLogin,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

